import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Tag } from 'antd';
import { searchOrderGenieProducts } from '../services/orderXApi';

const SEARCH_TYPE_OPTIONS = [
  { value: 'hybrid_semantic', label: 'Hybrid Semantic' },
  { value: 'hybrid_with_filters', label: 'Hybrid With Filters' },
  { value: 'exact_part_number', label: 'Exact Part Number' },
  { value: 'exact_manufacturer_part_number', label: 'Exact Manufacturer Part Number' },
  { value: 'exact_part_or_mfr', label: 'Exact Part Number OR Mfr Part Number' },
  { value: 'exact_supplier_part_number', label: 'Exact Supplier Part Number' },
  { value: 'exact_alt_part_number', label: 'Exact Alt Part Number' },
  { value: 'exact_any_part_number', label: 'Exact Part/Mfr/Alt/Supplier Part Number (any match)' },
  { value: 'cpn', label: 'CPN (Customer Part Number)' },
];
const PAGE_SIZE_OPTIONS = [6, 10, 20, 30, 50].map((value) => ({ value, label: `${value} / page` }));
const CONTROL_STYLE = { fontSize: '0.9rem', lineHeight: 1.2, height: 38 };
const INPUT_CONTROL_STYLE = {
  ...CONTROL_STYLE,
  boxShadow: 'none',
  outline: 'none',
};

const toProductCard = (row, index) => {
  const partNumber = String(row?.part_number || row?.productCode || '').trim();
  const manufacturerPartNumber = String(
    row?.manufacturer_part_number || row?.manufacturerPartNumber || ''
  ).trim();
  const title = String(row?.name || row?.short_desc || row?.description || partNumber || 'Product').trim();
  const description = String(row?.description || row?.long_desc || '').trim();
  const manufacturer = String(row?.manufacturer || row?.manufacturerName || '').trim();
  const category = String(row?.category || row?.category_name || '').trim();
  const imageUrl = String(row?.image || row?.imageUrl || row?.item_image || '').trim();
  const score = row?.score != null && row?.score !== '' ? Number(row.score) : null;
  const stableId = String(
    row?.product_id
      || row?.item_id
      || row?.itemId
      || partNumber
      || manufacturerPartNumber
      || index
  );
  return {
    id: stableId,
    title,
    description,
    partNumber,
    manufacturerPartNumber,
    manufacturer,
    category,
    imageUrl,
    score: Number.isFinite(score) ? score : null,
  };
};

const extractSearchDocs = (response) => {
  const topLevel = response && typeof response === 'object' ? response : {};
  const resultNode = topLevel?.result && typeof topLevel.result === 'object' ? topLevel.result : {};
  const candidates = [
    topLevel?.results,
    topLevel?.docs,
    resultNode?.docs,
    resultNode?.results,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
};

const OrderGenieV3ProductSearch = ({ headerCustomerId = '' }) => {
  const [searchType, setSearchType] = useState('hybrid_semantic');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [topK, setTopK] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [brokenImageMap, setBrokenImageMap] = useState({});
  const [meta, setMeta] = useState({ total: 0, loaded: 0, index_name: '' });
  const [debugInfo, setDebugInfo] = useState(null);
  const [runtimeStatus, setRuntimeStatus] = useState({
    engine: '',
    fallbackFrom: '',
    fallbackError: '',
    lastError: '',
  });

  const selectedType = String(searchType || '').trim().toLowerCase();
  const showFilters = selectedType === 'hybrid_with_filters';
  const showCpn = selectedType === 'cpn';
  const hasMore = Number(meta.loaded || 0) < Number(meta.total || 0);

  const productCards = useMemo(
    () => (Array.isArray(results) ? results : []).map((row, idx) => toProductCard(row, idx)),
    [results]
  );

  const buildPayload = (page = 1) => {
    const normalizedQuery = String(query || '').trim();
    const normalizedHeaderCustomerId = String(headerCustomerId || '').trim();
    const normalizedCustomerId = String(customerId || '').trim() || normalizedHeaderCustomerId;
    const isPartSearch = selectedType === 'exact_part_number' || selectedType === 'exact_part_or_mfr';
    const isManufacturerPartSearch =
      selectedType === 'exact_manufacturer_part_number' || selectedType === 'exact_part_or_mfr';
    const isSupplierPartSearch = selectedType === 'exact_supplier_part_number';
    const isAltPartSearch = selectedType === 'exact_alt_part_number';

    return {
      search_type: searchType,
      page,
      page_size: Math.max(1, Math.min(100, Number(topK || 10))),
      top_k: Math.max(1, Math.min(100, Number(topK || 10))),
      query: normalizedQuery || undefined,
      category: String(category || '').trim() || undefined,
      manufacturer: String(manufacturer || '').trim() || undefined,
      // All search modes are driven by the main search bar input.
      part_number: isPartSearch ? (normalizedQuery || undefined) : undefined,
      manufacturer_part_number: isManufacturerPartSearch ? (normalizedQuery || undefined) : undefined,
      supplier_part_number: isSupplierPartSearch ? (normalizedQuery || undefined) : undefined,
      alt_part_number: isAltPartSearch ? (normalizedQuery || undefined) : undefined,
      cpn: selectedType === 'cpn' ? (normalizedQuery || undefined) : undefined,
      customer_id: normalizedCustomerId || undefined,
      customer_id_erp: normalizedHeaderCustomerId || undefined,
      customer_name: String(customerName || '').trim() || undefined,
    };
  };

  const runSearch = async ({ append = false, page = 1 } = {}) => {
    setError('');
    setRuntimeStatus((prev) => ({ ...prev, lastError: '' }));
    const payload = buildPayload(page);

    if (!payload.query) {
      setError('Search input is required.');
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await searchOrderGenieProducts(payload);
      const nextResults = extractSearchDocs(response);
      setResults((prev) => {
        if (!append) return nextResults;
        const merged = [...(Array.isArray(prev) ? prev : []), ...nextResults];
        const dedupedByCode = new Map();
        merged.forEach((item, idx) => {
          const key = String(
            item?.productCode || item?.part_number || item?.item_id || item?.id || `row-${idx}`
          );
          if (!dedupedByCode.has(key)) {
            dedupedByCode.set(key, item);
          }
        });
        return Array.from(dedupedByCode.values());
      });
      if (!append) {
        setBrokenImageMap({});
      }
      const fallbackFrom = String(response?.fallback_from_engine || '').trim().toLowerCase();
      const fallbackError = String(response?.fallback_error || '').trim();
      setRuntimeStatus({
        engine: String(response?.search_engine || '').trim().toLowerCase(),
        fallbackFrom,
        fallbackError,
        lastError: fallbackFrom && fallbackError ? fallbackError : '',
      });
      setDebugInfo({
        topLevelKeys: Object.keys(response || {}),
        hasResults: Array.isArray(response?.results),
        hasDocs: Array.isArray(response?.docs),
        hasNestedResultDocs: Array.isArray(response?.result?.docs),
        count: response?.count,
        numFound: response?.numFound,
        nestedNumFound: response?.result?.numFound,
        collection: response?.collection || response?.result?.collection || response?.index_name || '',
        searchEngine: response?.search_engine || response?.result?.search_engine || '',
        fallbackFromEngine: response?.fallback_from_engine || '',
        fallbackError: response?.fallback_error || '',
        searchType: response?.search_request?.search_type || '',
        extractedDocs: nextResults.length,
      });
      setMeta({
        total: Number(
          response?.count
          ?? response?.numFound
          ?? response?.result?.numFound
          ?? nextResults.length
          ?? 0
        ),
        loaded: Number((append ? (Array.isArray(results) ? results.length : 0) : 0) + nextResults.length),
        index_name: String(
          response?.index_name
          || response?.collection
          || response?.result?.collection
          || ''
        ),
      });
    } catch (err) {
      const nextError = String(err?.message || err || 'Search failed');
      if (!append) {
        setResults([]);
        setMeta({ total: 0, loaded: 0, index_name: '' });
      }
      setDebugInfo(null);
      setRuntimeStatus((prev) => ({ ...prev, lastError: nextError }));
      setError(nextError);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (loading) return;
    await runSearch({ append: false, page: 1 });
  };

  const handleLoadMore = async () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = Math.floor((Array.isArray(results) ? results.length : 0) / Math.max(1, Number(topK || 10))) + 1;
    await runSearch({ append: true, page: nextPage });
  };

  return (
    <div className="container-fluid px-0">
      <Card className="shadow-sm border-0 mb-3">
        <form onSubmit={handleSubmit}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-xl-7">
            <label className="form-label fw-semibold small mb-1">Search</label>
            <div className="input-group">
              <select
                className="form-select shadow-none"
                value={searchType}
                onChange={(event) => setSearchType(event.target.value)}
                style={{ ...INPUT_CONTROL_STYLE, maxWidth: 240 }}
              >
                {SEARCH_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                className="form-control shadow-none"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products, part numbers, manufacturers..."
                style={INPUT_CONTROL_STYLE}
              />
              <button
                className="btn btn-primary px-3 shadow-none"
                type="submit"
                disabled={loading}
                style={CONTROL_STYLE}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <label className="form-label fw-semibold small mb-1">Top K</label>
            <select
              className="form-select shadow-none"
              value={topK}
              onChange={(event) => setTopK(Number(event.target.value) || 10)}
              style={INPUT_CONTROL_STYLE}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {showFilters && (
            <>
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold small mb-1">Category</label>
                <Input size="small" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Optional" />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold small mb-1">Manufacturer</label>
                <Input size="small" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="Optional" />
              </div>
            </>
          )}
          {showCpn && (
            <>
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold small mb-1">Customer ID</label>
                <Input
                  size="small"
                  value={customerId}
                  onChange={(e) => setCustomerId(e.target.value)}
                  placeholder={headerCustomerId ? `Header: ${headerCustomerId}` : 'Optional'}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold small mb-1">Customer Name</label>
                <Input size="small" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Optional" />
              </div>
            </>
          )}
        </div>
        {error && <div className="alert alert-danger mt-3 mb-0 py-2">{error}</div>}
        {(runtimeStatus.engine || runtimeStatus.lastError || runtimeStatus.fallbackFrom) && (
          <div
            className={`alert mt-3 mb-0 py-2 ${
              runtimeStatus.lastError && !runtimeStatus.fallbackFrom
                ? 'alert-danger'
                : (runtimeStatus.fallbackFrom && runtimeStatus.fallbackError ? 'alert-warning' : 'alert-info')
            }`}
          >
            <div className="small">
              <span className="fw-semibold">Search engine:</span>{' '}
              {runtimeStatus.engine ? runtimeStatus.engine.toUpperCase() : 'Unknown'}
              {runtimeStatus.fallbackFrom ? ` (fallback from ${runtimeStatus.fallbackFrom.toUpperCase()})` : ''}
            </div>
            {(runtimeStatus.lastError || runtimeStatus.fallbackError) && (
              <div className="small mt-1">
                <span className="fw-semibold">Error:</span> {runtimeStatus.lastError || runtimeStatus.fallbackError}
              </div>
            )}
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 mt-3">
          <Tag color="blue">Loaded: {Array.isArray(results) ? results.length : 0}</Tag>
          <Tag color="geekblue">Total: {meta.total}</Tag>
          {meta.index_name ? (
            <Tag>
              {runtimeStatus.engine === 'solr' ? 'Collection' : 'Index'}: {meta.index_name}
            </Tag>
          ) : null}
        </div>
        {debugInfo && (
          <div className="alert alert-secondary mt-3 mb-0 py-2 small">
            <div className="fw-semibold mb-1">Response debug</div>
            <div>
              keys: {debugInfo.topLevelKeys.join(', ') || '-'} | extracted_docs: {debugInfo.extractedDocs}
            </div>
            <div>
              has_results: {String(debugInfo.hasResults)} | has_docs: {String(debugInfo.hasDocs)} | has_result_docs:{' '}
              {String(debugInfo.hasNestedResultDocs)}
            </div>
            <div>
              count: {String(debugInfo.count ?? '-')} | numFound: {String(debugInfo.numFound ?? '-')} | result.numFound:{' '}
              {String(debugInfo.nestedNumFound ?? '-')}
            </div>
            <div>
              collection/index: {debugInfo.collection || '-'} | engine: {debugInfo.searchEngine || '-'} | type:{' '}
              {debugInfo.searchType || '-'}
            </div>
            <div>
              fallback_from: {debugInfo.fallbackFromEngine || '-'} | fallback_error: {debugInfo.fallbackError || '-'}
            </div>
          </div>
        )}
        </form>
      </Card>

      <Card className="shadow-sm border-0">
        {productCards.length === 0 ? (
          <div className="text-muted py-3">No results yet.</div>
        ) : (
          <div className="row g-3">
            {productCards.map((product) => (
              <div className="col-12 col-sm-6 col-xl-4 col-xxl-3" key={product.id}>
                <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: 14 }}>
                  <div
                    className="w-100 bg-light d-flex align-items-center justify-content-center overflow-hidden rounded-top"
                    style={{ minHeight: 130, maxHeight: 130 }}
                  >
                    {product.imageUrl && !brokenImageMap[product.id] ? (
                      <img
                        src={product.imageUrl}
                        alt={product.title}
                        className="h-100"
                        style={{ width: '72%', objectFit: 'contain' }}
                        onError={() =>
                          setBrokenImageMap((prev) => ({
                            ...(prev || {}),
                            [product.id]: true,
                          }))
                        }
                      />
                    ) : (
                      <div className="text-muted small">No image</div>
                    )}
                  </div>
                  <div className="card-body d-flex flex-column py-2 px-3">
                    <h6
                      className="card-title mb-1"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 36 }}
                    >
                      {product.title}
                    </h6>
                    <div className="text-muted small mb-1 text-truncate">
                      {product.manufacturer || 'Unknown manufacturer'}
                      {product.category ? ` • ${product.category}` : ''}
                    </div>
                    <p
                      className="text-muted small mb-1"
                      style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 32 }}
                    >
                      {product.description || 'No description available.'}
                    </p>
                    <div className="mt-auto">
                      <div className="small text-truncate">
                        <span className="fw-semibold">Part #:</span> {product.partNumber || '-'}
                      </div>
                      <div className="small text-truncate">
                        <span className="fw-semibold">Mfr Part #:</span> {product.manufacturerPartNumber || '-'}
                      </div>
                      <div className="small mt-1">
                        <span className="fw-semibold">Score:</span>{' '}
                        {product.score != null ? product.score.toFixed(4) : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {hasMore && (
          <div className="d-flex justify-content-center mt-3">
            <Button onClick={handleLoadMore} loading={loadingMore} disabled={loading || loadingMore}>
              Load More
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default OrderGenieV3ProductSearch;
